import {v4 as uuid4} from 'uuid'
import { QRCODE_END_OF_FILE } from '../constants/qrCodes'
import {OBJECT_TYPE_RECEIPT} from '../constants/receipts'
import { OBJECT_TYPE_TICKET } from '../constants/ticket'
import {RESULT_SUCCESS, RESULT_WARNING, RESULT_ERROR} from "../constants/results"

export const generateTicketsFromProductVariant = (
    payeeProduct,
    variantId="",
    numberOfTickets=0,
    lastTicketNumber=0,
    payeeCatalogId="",
    batchId=uuid4()
) => {
    if (!payeeProduct) return []
    const {variantsById={}, name="", id:payeeProductId="", payeeId, hasVariants=false} = payeeProduct
    const variant = variantsById[variantId]
    if (!variant) return []
    const {price, variantDetails=""} = variant
    const ticketVariantDetails = hasVariants ? variantDetails : ""
    const tickets = []
    for(let i = lastTicketNumber; i < lastTicketNumber + numberOfTickets; i++){
        const ticketNumber = i + 1
        tickets.push({
            id: uuid4(),
            name,
            payeeId,
            payeeProductId,
            payeeCatalogId,
            variantId,
            variantDetails: ticketVariantDetails,
            price,
            quantity: 1,
            ticketNumber,
            batchId,
            physical: true,
            verifiedAt: null,
        })
    }
    return tickets
}

export const generateQRDataFromObject = (object={}, objectType="") => {
    if (!object || typeof object !== "object") return ""
    const qrData = JSON.stringify({
        id: object.id || "", 
        objectType
    })
    return `${qrData}${QRCODE_END_OF_FILE}`
}

export const customValidateProductReceiptTickets = (scanData, receipt, currentPayeeProductId) => {
    //custom validation function for use in verifyTicketScanData for product-centric receipt flows
    //check that the payee product in the scan data provided matches with the active payee product
    if (scanData.payeeProductId !== currentPayeeProductId){ //not the current product
        return {
            success: false, 
            result: RESULT_WARNING,
            resultMessage: "Wrong product"
        }
    } else return ({
        success: true
    })
}

export const getVerificationsAndQuantityFromLineItem = (receipt, scanData={}) => {
    //product / receipt centric function for use with product receipt flow uses of verifyTicketScanData
    //instead of getting verificationsById from the main object, we retrieve it from receipt.lineItemsById
    //the id of the scan data is the product.variantId
    const lineItem = receipt.lineItemsById[scanData.id || ""]
    const {verificationsById={}, quantity} = lineItem || {}
    return {
        verificationsById,
        quantity
    }
}

export const getDeterministicVerificationIdFromLineItem = (receipt, scanData, numOfVerifications) => {
    //product / receipt centric function for getting a deterministic verification id for use with product receipt flow uses of verifyTicketScanData
    //instead of getting verificationsById from the main object only, we combine it with receipt.lineItemsById

    const lineItem = receipt.lineItemsById[scanData.id || ""]
    return `${numOfVerifications}${receipt.id}${lineItem.id}`
}

export const verifyTicketScanData = (
    scanData={}, 
    objectsById={}, 
    objectType="", 
    currentPayeeId, 
    customValidate,
    getVerificationsAndQuantity = (o, scanData) => ({verificationsById: o.verificationsById, quantity: o.quantity}),
    getDeterministicVerificationId = (o, scanData, numOfVerifications) => `${numOfVerifications}${o.id}`
) => {
    /**
     * Purpose: Verify that the data on a QR scan matches the receipt, ticket or other object in the DB and that it is valid 
     * 
     * Method
     * 1. Is the scan data valid? Successfully retrieve the object
     * 2. Is the scan data relevant to the  current payee? Ensure it has the payee id we are checking for
     * 3. Has the scan data been used up? Verified more times than the quantity allowed 
     */
    const results = {success: false}
    //1. is the scan data valid?
    let objectId 
    if (objectType === OBJECT_TYPE_RECEIPT){
        const {receiptId=""} = scanData || {}
        objectId = receiptId
    } else if (objectType === OBJECT_TYPE_TICKET){
        objectId = scanData?.id
    }
    
    const object = objectsById[objectId]
    //no such receipt, ticket or other object
    if (!object) {
        results.result = RESULT_ERROR
        results.resultMessage = "Ticket Not found"
        return results
    }
    //2. is the receipt relevant to the current payee?
    if (object.payeeId !== currentPayeeId){//not the current payee
        results.result = RESULT_WARNING
        results.resultMessage = "Wrong merchant account"
        return results
    }

    //execute custom validation here
    if (typeof customValidate === "function"){
        const customResults = customValidate(scanData, object)
        if (customResults && !customResults?.success){
            return {
                success: false,
                result: customResults.result,
                resultMessage: customResults.resultMessage || ""
            }
        }
    }
    //3. Has the receipt been used up
    const {verificationsById={}, quantity=0} = getVerificationsAndQuantity(object, scanData) || {}
    //TODO, may need to change this if we make it possible to verify more than one qty at once, per scan
    //e.g. letting 2 people into an event and saying "admit 2"
    const numOfVerifications = Object.keys(verificationsById).length
    if (numOfVerifications >= quantity) {
        results.result = RESULT_WARNING
        results.resultMessage = "Already used"
        return results
    }
    //if everything is good, it is verified successfully
    const resultMessage = quantity > 1 ? `All good: ${numOfVerifications + 1} used out of ${quantity}`
                          : "All good!"
    return {
        success: true,
        resultMessage,
        result: RESULT_SUCCESS,
        // Used a deterministic Id to avoid the possibility of using up more than one of a ticket's scans at once
        verificationId: getDeterministicVerificationId(object, scanData, numOfVerifications),
        usedCount: numOfVerifications + 1,
    }
}

export const decodeTicketQRCodeData = (qrString="") => {
    //if not string return
    if (!qrString || typeof qrString !== 'string') return null
    //if string does not contain the QR code end of file marker, return
    if (!qrString.endsWith(QRCODE_END_OF_FILE)) return null
    //remove the end of file marker
    qrString = qrString.replace(QRCODE_END_OF_FILE, "")
    return JSON.parse(qrString) 
}